import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { User, Shield, ShieldAlert, Ban, Trash2, Search, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    try {
      await axios.put(`${API}/admin/users/${editUser.id}`, {
        role: editUser.role,
        is_banned: editUser.is_banned,
        name: editUser.name,
      });
      toast.success("User updated");
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteConfirm.id}`);
      toast.success("User deleted");
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const toggleBan = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_banned: !user.is_banned,
      });
      toast.success(user.is_banned ? "User unbanned" : "User banned");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const filtered = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.role?.toLowerCase().includes(search.toLowerCase())
  );

  const roleIcon = (role) => {
    if (role === "admin") return <Shield className="w-3.5 h-3.5 text-amber-400" />;
    if (role === "moderator") return <ShieldAlert className="w-3.5 h-3.5 text-sky-400" />;
    return <User className="w-3.5 h-3.5 text-slate-400" />;
  };

  const roleBadge = (role) => {
    const styles = {
      admin: "bg-amber-500/15 text-amber-400 border-amber-500/25",
      moderator: "bg-sky-500/15 text-sky-400 border-sky-500/25",
      user: "bg-slate-500/15 text-slate-400 border-slate-500/25",
    };
    return styles[role] || styles.user;
  };

  return (
    <AdminLayout title="Manage Users">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400 text-sm">{users.length} registered users</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:border-amber-500/50 focus:outline-none w-64"
            data-testid="user-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">User</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Role</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Joined</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500 tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center flex-shrink-0">
                        {u.avatar ? (
                          <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{u.name || "Unnamed"}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${roleBadge(u.role)}`}>
                      {roleIcon(u.role)}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {u.is_banned ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-400">
                        <Ban className="w-3.5 h-3.5" /> Banned
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                        <Check className="w-3.5 h-3.5" /> Active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditUser({ ...u })}
                        className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-amber-400 transition-colors"
                        title="Edit user"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleBan(u)}
                        className={`p-1.5 rounded hover:bg-white/10 transition-colors ${u.is_banned ? "text-emerald-400" : "text-slate-400 hover:text-red-400"}`}
                        title={u.is_banned ? "Unban user" : "Ban user"}
                        data-testid={`ban-user-${u.id}`}
                      >
                        <Ban className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(u)}
                        className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-red-400 transition-colors"
                        title="Delete user"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-[#0B0F19] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs font-mono text-slate-400 mb-1 block">Name</label>
                <input
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="text-xs font-mono text-slate-400 mb-1 block">Email</label>
                <input
                  value={editUser.email}
                  disabled
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-500 text-sm cursor-not-allowed"
                />
              </div>
              <div>
                <label className="text-xs font-mono text-slate-400 mb-1 block">Role</label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                  data-testid="edit-user-role"
                >
                  <option value="user">User</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-mono text-slate-400">Banned</label>
                <button
                  onClick={() => setEditUser({ ...editUser, is_banned: !editUser.is_banned })}
                  className={`w-10 h-5 rounded-full transition-colors ${editUser.is_banned ? "bg-red-500" : "bg-slate-600"}`}
                  data-testid="edit-user-ban-toggle"
                >
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${editUser.is_banned ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)} data-testid="edit-user-cancel">Cancel</Button>
            <Button onClick={handleUpdate} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="edit-user-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0B0F19] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-slate-400 text-sm">
            Are you sure you want to permanently delete <span className="text-white font-medium">{deleteConfirm?.name || deleteConfirm?.email}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)} data-testid="delete-user-cancel">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-user-confirm">Delete User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsers;
